import { useEffect, useRef } from 'react'
import clsx from 'clsx'
import { FeedCardHeader } from 'src/components/feedCard/components'
import { useMentionUtils } from 'src/components/postCommentInput/hooks'
import { UserProfileUtils, useTrackEvents } from 'src/context/userProfileContext'
import { ComponentName } from 'src/utils/datahubDefaultEvent'
import { generatePath, Link, useParams } from 'react-router-dom'
import { paths } from 'src/router/paths'
import { ACCESS_TO_POST } from 'src/utils/constants'
import i18n from 'src/i18n'
import useSendDataHubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume'
import { Actions, ArrivedFrom, Entities, EventNames, ScreenNames } from 'src/utils/datahub/enums'
import { MediaDisplayFormat, PostMediaThumbnail } from 'src/components/PostMediaThumbnail'
import { PostType } from 'src/hook/usePosts'
import { useUserProfile } from 'src/components/globalUserProfile'
import { MediaWrapper, PostDescription, PostTitle, ViewMore } from './card.styles'
import { CardProps, UrlPathParams } from './card.types'

const Card = ({ post, borderBottom }: CardProps) => {
  const postDescriptionRef = useRef<HTMLDivElement>(null)
  const { lang, membership } = useParams<UrlPathParams>()
  const { convertMentionToHTML } = useMentionUtils()
  const { loggedUserUcode, onOpen: onOpenUserProfile } = useUserProfile()
  const { sendOpenProfileEvent } = useTrackEvents({ loggedUserUcode })
  const { sendDataHubConsume } = useSendDataHubConsume()

  const postBasePath = generatePath(paths.COMMUNITY_POST_PATH, {
    lang,
    membership,
    idCommunity: String(post?.ownerCollectionId),
    idPost: String(post?.id)
  })

  const postPath = `${postBasePath}?origin=${ACCESS_TO_POST.COMMUNITY_POST_LISTING}`

  const description = convertMentionToHTML({
    text: post?.description as string,
    mentions: post?.mentions?.map((mention) => ({
      tag: mention?.tag,
      failed: Boolean(mention?.failed),
      failureReason: mention?.failureReason,
      ucode: mention?.metadata?.ucode,
      name: mention?.metadata?.name
    }))
  })

  const showMedia = post.postType === PostType.IMAGE || post.postType === PostType.VIDEO

  const handleClickOnPost = () => {
    sendDataHubConsume({
      action: Actions.VIEW,
      entity: Entities.COMMUNITY,
      event: {
        screenName: ScreenNames.COMMUNITY_POST_DETAIL,
        eventName: EventNames.CLICK_TO_GO_TO_POST_DETAIL,
        communityId: Number(post?.ownerCollectionId),
        communityName: post?.ownerCollectionName,
        postId: Number(post.id),
        postType: post.postType,
        arrivedFrom: ArrivedFrom.COMMUNITY_FEED_V3
      }
    })
  }

  useEffect(() => {
    const elementsToOpenProfile = postDescriptionRef.current?.querySelectorAll(
      '[data-action="open-profile"]'
    )

    const callback = ({ customEvent, ucode }) => {
      onOpenUserProfile(customEvent, ucode)
      sendOpenProfileEvent({
        componentName: ComponentName.POST_FEED,
        userUcode: ucode,
        accessFromMention: true
      })
    }

    const removeListener = UserProfileUtils.openEventListener({
      elements: elementsToOpenProfile,
      componentName: ComponentName.POST_FEED,
      callback
    })

    return removeListener
  }, [])

  return (
    <div className="_pt-3">
      <FeedCardHeader post={post} className="_px-5" />

      <div
        className={clsx('_pb-6 _mx-5', {
          '_border-bottom _border-gray-200': borderBottom
        })}
      >
        <div className="_d-flex _justify-content-between _align-items-start _w-full">
          <Link
            to={postPath}
            title={i18n.t('pin_post.view_full_post')}
            className="_text-decoration-none _mb-1"
            onClick={handleClickOnPost}
          >
            <PostTitle
              title={post.title}
              className="_text-2 _text-gray-900 _font-weight-bold _mb-1"
            >
              {post.title}
            </PostTitle>

            {post?.description && (
              <PostDescription
                ref={postDescriptionRef}
                className="_font-14 _text-gray-900 _mb-1"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}

            <ViewMore className="_font-14 _text-gray-700 _mt-2">
              {i18n.t('pin_post.view_full_post')}
            </ViewMore>
          </Link>

          {showMedia && (
            <MediaWrapper className="_ml-4 _mb-1 _flex-shrink-0">
              <PostMediaThumbnail
                key={post.id}
                id={post.id}
                mediaType={post.postType}
                displayFormat={MediaDisplayFormat.Square}
              />
            </MediaWrapper>
          )}
        </div>
      </div>
    </div>
  )
}

export default Card
